import { useState, useEffect } from "react";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { carrierNav } from "@/pages/carrier/nav";
import { Metric, DemoBadge } from "@/components/common";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Loader2, Zap } from "lucide-react";

export default function XDMoney() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/carrier/xd-money").then((r) => setD(r.data)); }, []);
  if (!d) return <DashboardShell nav={carrierNav}><div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-orange" size={32} /></div></DashboardShell>;

  return (
    <DashboardShell nav={carrierNav}>
      <div className="flex items-center gap-3 mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Revenue & Payments</h1>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric testid="money-today" label="Revenue Today" value={`$${d.revenue.today.toLocaleString()}`} accent="blue" />
        <Metric testid="money-week" label="This Week" value={`$${d.revenue.week.toLocaleString()}`} accent="blue" />
        <Metric testid="money-month" label="This Month" value={`$${d.revenue.month.toLocaleString()}`} accent="blue" />
        <Metric testid="money-year" label="This Year" value={`$${d.revenue.year.toLocaleString()}`} accent="blue" />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 bg-xd-paper border border-xd-border rounded-xl p-6">
          <h2 className="font-heading text-lg font-bold mb-4">Revenue vs Expenses</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={d.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D2D2D" vertical={false} />
              <XAxis dataKey="month" stroke="#737373" fontSize={12} />
              <YAxis stroke="#737373" fontSize={12} />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid #2D2D2D", borderRadius: 8, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="revenue" fill="#007AFF" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" fill="#FF5500" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-4">
          <Row label="Loaded Miles" value={d.loaded_miles.toLocaleString()} />
          <Row label="Deadhead Miles" value={d.deadhead_miles.toLocaleString()} />
          <Row label="Avg Rate / Mile" value={`$${d.avg_rate_mile}`} />
          <Row label="Fuel Expenses" value={`$${d.fuel_expenses.toLocaleString()}`} />
          <Row label="Other Expenses" value={`$${d.other_expenses.toLocaleString()}`} />
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Metric testid="money-outstanding" label="Outstanding Invoices" value={`$${d.outstanding_invoices.toLocaleString()}`} accent="orange" />
        <Metric testid="money-paid" label="Paid Invoices" value={`$${d.paid_invoices.toLocaleString()}`} accent="green" />
        <Metric testid="money-profit" label="Estimated Profit" value={`$${d.estimated_profit.toLocaleString()}`} accent="green" />
      </div>

      <div className="mt-6 rounded-xl border border-xd-border bg-gradient-to-r from-xd-blue/15 to-xd-paper p-6 flex items-center justify-between gap-4 flex-wrap">
        <div><h3 className="font-heading text-lg font-bold">Get Paid Faster</h3><p className="text-sm text-xd-muted">Factoring & Quick Pay — contact XD to enable fast payouts on your account.</p></div>
        <Button data-testid="get-paid-btn" className="bg-xd-blue hover:bg-xd-blueH text-white" onClick={() => {}}><Zap size={16} className="mr-2" /> Get Paid Faster</Button>
      </div>
    </DashboardShell>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between bg-xd-paper border border-xd-border rounded-lg px-4 py-3"><span className="text-sm text-xd-muted">{label}</span><span className="font-mono font-bold">{value}</span></div>;
}
